import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Edit2 } from "react-feather";
import toast from "react-hot-toast";
import { useGetQuotationPrototypeQuery, useSetQuotationPrototypeActiveMutation } from "../../quotationsApi";
import { useCanManagePrototype } from "../../useQuotationPermissions";
import QuotationLineLabel, { quotationLineTypeLabel } from "../../components/QuotationLineLabel";
import PrototypePrintButton from "../components/PrototypePrintButton";
import { paths } from "../../../../../routes/paths";

const money = (n) => Number(n || 0).toFixed(2);

const PrototypeDetail = () => {
  const { recordGuid } = useParams();
  const navigate = useNavigate();
  const canManage = useCanManagePrototype();
  const { data: prototype, isLoading, isError, error } = useGetQuotationPrototypeQuery(recordGuid);
  const [setActive, { isLoading: isToggling }] = useSetQuotationPrototypeActiveMutation();

  if (isLoading) return <div className="page-wrapper"><div className="content"><p>Loading...</p></div></div>;
  if (isError || !prototype) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Link to={paths.quotationPrototypes} className="d-inline-flex align-items-center mb-3"><ArrowLeft size={16} className="me-1" /> Back to Prototypes</Link>
          <div className="alert alert-danger">Could not load this prototype ({error?.message || "server error"}).</div>
        </div>
      </div>
    );
  }

  const handleToggleActive = async () => {
    try {
      await setActive({ recordGuid, isActive: !prototype.isActive }).unwrap();
      toast.success(prototype.isActive ? "Prototype deactivated" : "Prototype reactivated");
    } catch (e) {
      toast.error(e?.message || "Could not update the prototype");
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <Link to={paths.quotationPrototypes} className="d-inline-flex align-items-center mb-2"><ArrowLeft size={16} className="me-1" /> Back to Prototypes</Link>
            <h4>
              {prototype.name}{" "}
              {!prototype.isActive && <span className="badge badge-linesecondary">Inactive</span>}
            </h4>
            <h6>{prototype.branchCode}</h6>
          </div>
          {/* Print is for anyone who can open the prototype; Edit / Deactivate stay with the managers. */}
          <div className="page-btn d-flex gap-2">
            <PrototypePrintButton prototype={prototype} />
            {canManage && (
              <>
              <button type="button" className="btn btn-outline-primary d-inline-flex align-items-center gap-1" onClick={() => navigate(paths.quotationPrototypeEdit(recordGuid))}>
                <Edit2 size={14} /> Edit
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={handleToggleActive} disabled={isToggling}>
                {prototype.isActive ? "Deactivate" : "Reactivate"}
              </button>
              </>
            )}
          </div>
        </div>

        {prototype.description && <p className="text-muted">{prototype.description}</p>}

        <div className="card mb-3">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-sm mb-0">
                <thead>
                  <tr><th>#</th><th>Type</th><th>Description</th><th>Unit</th><th className="text-end">Qty</th><th className="text-end">Rate</th><th className="text-end">VAT %</th></tr>
                </thead>
                <tbody>
                  {prototype.lines.map((l, idx) => (
                    <tr key={l.lineId}>
                      <td>{idx + 1}</td>
                      <td>{quotationLineTypeLabel(l)}</td>
                      <td><QuotationLineLabel line={l} /></td>
                      <td>{l.unit || "-"}</td>
                      <td className="text-end">{l.qty}</td>
                      <td className="text-end">{money(l.rate)}</td>
                      <td className="text-end">{l.vatPercent}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="text-muted small">Created by {prototype.createdByName} on {new Date(prototype.createdDate).toLocaleString()}</div>
      </div>
    </div>
  );
};

export default PrototypeDetail;
